import type { Article } from './content-types';
import { articleBodyPlainText } from './article-body';

/**
 * Shape consumed by the `<Article>` card component (and its grids).
 * Centralizing the transformation here keeps SSR pages slim and prevents
 * each call site from rebuilding the same handful of derived strings.
 */
export interface ArticleCardProps {
    $id: string;
    slug: string;
    title: string;
    /** Editorial kicker shown as a small eyebrow above the title. */
    trailer?: string;
    description: string;
    /** First hero cover URL (same as `covers[0]` when the gallery is set). */
    cover: string;
    /**
     * Ordered hero cover URLs for fading galleries on cards / homepage.
     * Empty when the article has no main images.
     */
    covers: string[];
    published: boolean;
    author?: string;
    createdAt?: string;
    isLiveReport?: boolean;
    /** Primary section display name, when the article belongs to a category. */
    categoryName?: string;
    /** Primary section key (e.g. "sports") for category links. */
    categoryKey?: string;
    /**
     * When set, cards and `/article/$id` should send readers here instead of
     * the local article page (admin "כתובת הפניה").
     */
    redirect?: string;
}

/**
 * Normalize an article redirect / external URL for use as `href`.
 * Allows same-origin paths (`/…`) and http(s) URLs; rejects other schemes.
 */
export function articleExternalHref(
    redirect?: string | null,
): string | undefined {
    const trimmed = redirect?.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
    try {
        const withScheme = /^(https?:)?\/\//i.test(trimmed)
            ? trimmed
            : `https://${trimmed}`;
        const url = new URL(withScheme);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return undefined;
        }
        return url.href;
    } catch {
        return undefined;
    }
}

/**
 * Live-report badges go stale fast: once a story is more than 24 hours
 * past publish time it's no longer "live", regardless of the editor flag.
 */
export const LIVE_REPORT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** True when the article is flagged live AND still within the live window. */
export function isLiveReportActive(live: boolean, date: string | Date): boolean {
    if (!live) return false;
    const ts = new Date(date).getTime();
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts <= LIVE_REPORT_MAX_AGE_MS;
}

/** Prefer card thumb, then cover crop, then mid-width gallery preview. */
export function heroCoverUrls(article: Article): string[] {
    return (article.heroImages ?? [])
        .map((img) => img.thumbUrl ?? img.coverUrl ?? img.url)
        .filter((url): url is string => !!url);
}

/** Map a domain Article into the flat props the card component renders. */
export function toCard(article: Article): ArticleCardProps {
    const covers = heroCoverUrls(article);
    return {
        $id:          article.$id,
        slug:         article.slug,
        title:        article.title,
        trailer:      article.trailer,
        // Subtitle is the editorial standfirst; legacy editors used it as the
        // card description, so prefer it and fall back to a clipped body.
        description:  article.subtitle
            ?? articleBodyPlainText(article.body).slice(0, 200),
        cover:        covers[0] ?? article.coverThumbUrl ?? article.coverUrl ?? '',
        covers,
        published:    article.status === 'published',
        author:       article.authorName,
        createdAt:    article.date,
        isLiveReport: isLiveReportActive(article.live, article.date),
        categoryName: article.categories[0]?.name,
        categoryKey:  article.categoryKeys[0],
        redirect:     article.redirect,
    };
}
